import React, { Fragment, useState, ReactElement, useMemo } from "react";
import { cloneDeep, sortBy } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { Button } from "../../../../../components/src/Button";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";
import { Link, useLocation } from "wouter";

import { recursivelyRemoveKey } from "../../../../../common/src";

import { useAlertTimeOut } from "../../../../../components/src/common/Utils";
import { YesNoModal } from "../../../../../components/src/YesNoModal";
import { SubmitButton, TextField, SelectField, DateField, NullableSelectField, SearchListField } from "../../../../../components/src/form";
import { useMutation } from "urql";
import { CREATE_PARTY_BASE, UPDATE_PARTY_BASE } from "../../../../../graphql/src/party/queries";
import { PartyPageData } from "../PartyPage";
import {
    Party,
    PartyBaseInput,
    PartyType,
    PartyStandardSettlementInstructionType,
    TimezoneEnum,
    CurrencyEnum,
    AccountingCompanyTypeEnum,
    PartyStatusEnum,
    IssuerTypeEnum,
    CollectionNameEnum,
    AccountingFrequencyEnum,
    SsiTradeType,
    CountryCodeEnum,
    CountryCodeEnumDescriptions,
    NaceClass,
    NaceClassDescriptions
} from "../../../types.generated";
import { formikUrqlErrorFormater } from "../../../../../components/src/common/formik-urql-error-helper";
import { defaultAlias } from "../../../components/alias";
import { MarkDownField } from "../../../components/form/MarkDownField";
import { REACT_APP_API_URI } from "../../../env";

const NaceOptions: ({ _id: string; name: string } & {
    [key: string]: any;
})[] = Object.keys(NaceClass).map((naceKey) => ({
    _id: naceKey,
    name: naceKey + " - " + NaceClassDescriptions[naceKey]
}));

let countryOptions: { key: string; value: string }[] = Object.keys(CountryCodeEnum).map((country) => {
    return {
        key: country,
        value: country + " - " + CountryCodeEnumDescriptions[country]
    };
});
countryOptions = sortBy(countryOptions, "key");

const defaultPartyBase: PartyBaseInput = {
    _id: "000000000000000000000000",
    name: "",
    longName: "",
    issuerType: IssuerTypeEnum.None,
    status: PartyStatusEnum.Confirmed,
    number: 0,
    inLedgerTradeDate: "1900-01-01",
    description: "",
    sustainalyticsId: "",
    types: [],
    standardSettlementInstructions: [],
    creditRatings: [],
    aliases: [],
    accountingCurrency: CurrencyEnum.SEK,
    accountingFrequency: AccountingFrequencyEnum.None,
    accountingCompanyType: AccountingCompanyTypeEnum.None
};

export const PartyBaseTab = ({ data, refetch }: { data: PartyPageData; refetch: () => void }): ReactElement => {
    const [, navigate] = useLocation();

    const [_, createPartyBase] = useMutation(CREATE_PARTY_BASE);
    const [__, updatePartyBase] = useMutation(UPDATE_PARTY_BASE);

    const [modal, setModal] = useState({ showModal: false, payload: null });
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });
    useAlertTimeOut(alert, setAlert, 5);

    const party: PartyBaseInput | Partial<Party> = useMemo(() => {
        if (data.party._id === "000000000000000000000000") {
            return cloneDeep(defaultPartyBase);
        }
        return data.party;
    }, [data]);

    if (!party) return <div></div>;

    const isEdit = party._id === "000000000000000000000000" ? false : true;

    const defaultSettlementInstruction = {
        agentBusinessIdentifierCode: "-",
        beneficiaryBusinessIdentifierCode: "-",
        iban: "",
        securityAccount: "",
        buyerSellerSafe: "",
        placeOfSettlementId: data.placesOfSettlement && data.placesOfSettlement.length > 0 ? data.placesOfSettlement[0]._id : "",
        type: Object.values(PartyStandardSettlementInstructionType)[0],
        tradeType: SsiTradeType.Security
    };

    const defaultCreditRating = {
        creditRatingAgencyId: data.creditRatingAgencies && data.creditRatingAgencies.length > 0 ? data.creditRatingAgencies[0]._id : "",
        grade: ""
    };

    const parties = sortBy(data.parties, "name") as unknown as { _id: string; name: string }[];
    parties.unshift({ _id: "000000000000000000000000", name: "None" });

    const instruments = sortBy(data.instruments, "name") as unknown as { _id: string; name: string }[];
    instruments.unshift({ _id: "000000000000000000000000", name: "None" });

    const aliasOptions = (collection: CollectionNameEnum) => {
        if (collection === CollectionNameEnum.Party) {
            return parties;
        } else if (collection === CollectionNameEnum.Instrument) {
            return instruments;
        } else {
            return [{ _id: "000000000000000000000000", name: "None" }];
        }
    };

    const aliasLabel = (collection: CollectionNameEnum, documentId: string) => {
        if (collection === CollectionNameEnum.Party) {
            return <Link to={"/parties/" + documentId}>{collection}</Link>;
        } else if (collection === CollectionNameEnum.Instrument) {
            return <Link to={"/instruments/" + documentId}>{collection}</Link>;
        } else {
            return collection;
        }
    };

    return (
        <div className="mx-3">
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete party with id " + modal.payload.partyId + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        updatePartyBase({ partyId: modal.payload.partyId, input: modal.payload.input }).then(() => {
                            setAlert({
                                color: "success",
                                visible: true,
                                message: `The party has been updated and deleted successfully!`
                            });
                            refetch();
                        });
                    }}
                />
            ) : null}
            <div className="mb-4 flex flex-wrap mb-5">Basic information on Party.</div>
            <div id="partybaseform" className="partybaseform form">
                <Formik
                    enableReinitialize={true}
                    validateOnMount={true}
                    initialValues={party}
                    validate={(validateValues) => {
                        const errors = {};
                        if (validateValues.types.includes(PartyType.Client) && validateValues.timezone === null) {
                            errors["timezone"] = "timezone is required for clients";
                        }
                        return Object.keys(errors).length > 0 ? errors : {};
                    }}
                    validationSchema={Yup.object({
                        name: Yup.string().required().min(2),
                        longName: Yup.string().required().min(2),
                        creditRatings: Yup.array().of(
                            Yup.object().shape({
                                creditRatingAgencyId: Yup.string().required(),
                                grade: Yup.string().matches(/^[0-9aA-zZ\-+]+$/, "Only  0-9aA-zZ\\-\\+ allowed")
                            })
                        ),
                        creditRatingScale: Yup.array().of(Yup.string().matches(/^[0-9aA-zZ\-+]+$/, "Only  0-9aA-zZ\\-\\+ allowed"))
                    })}
                    onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                        const input: any = recursivelyRemoveKey(submitValues, "__typename");
                        delete input.updateUserId;
                        delete input.updateUserInfo;
                        if (isEdit) {
                            delete input.instruments;
                            delete input.issuer;
                            delete input.fundInfo;
                            delete input.externalAccounts;
                            delete input.accounts;

                            if (input.status && input.status === PartyStatusEnum.Deleted) {
                                setModal({ showModal: true, payload: { partyId: party._id, input } });
                            } else {
                                await updatePartyBase({ partyId: party._id, input })
                                    .then((result) => {
                                        if ("error" in result && result.error) {
                                            const message = formikUrqlErrorFormater(result.error, setErrors);
                                            setAlert({ color: "danger", visible: true, message });
                                        } else {
                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `The party has been updated successfully!`
                                            });
                                            refetch();
                                        }
                                    })
                                    .catch((error) => {
                                        setAlert({ color: "danger", visible: true, message: error.toString() });
                                    })
                                    .finally(() => {
                                        setSubmitting(false);
                                    });
                            }
                        } else {
                            await createPartyBase({ partyId: "000000000000000000000000", input })
                                .then((result) => {
                                    if ("error" in result && result.error) {
                                        const message = formikUrqlErrorFormater(result.error, setErrors);
                                        setAlert({ color: "danger", visible: true, message });
                                    } else {
                                        navigate("/parties/" + result.data.party._id, { replace: true });
                                    }
                                })
                                .catch((error) => {
                                    setAlert({ color: "danger", visible: true, message: error.toString() });
                                });
                        }
                    }}
                >
                    {({ isSubmitting, values }) => {
                        return (
                            <Fragment>
                                <Form autoComplete="off">
                                    <div className="flex flex-wrap -mx-2 mb-2">
                                        <div className="w-full px-2 mb-2">
                                            <MarkDownField
                                                name="description"
                                                graphqlApiBaseUrl={REACT_APP_API_URI}
                                                label="Description"
                                                className="w-full"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-2">
                                        {/* Column 1 */}
                                        <div className="w-full md:w-4/12 px-2 mb-2">
                                            {isEdit ? (
                                                <TextField
                                                    name="_id"
                                                    label="Id"
                                                    type="text"
                                                    className="w-full mb-2"
                                                    disabled={true}
                                                />
                                            ) : null}
                                            <TextField
                                                name="name"
                                                label="Name*"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="longName"
                                                label="Long name"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="address"
                                                label="Address"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <SelectField
                                                className="w-full mb-2"
                                                name={"country"}
                                                label={"Country"}
                                                options={countryOptions}
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="website"
                                                label="Website"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="phoneNumber"
                                                label="Phone number"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="ceo"
                                                label="CEO"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="complaintsManager"
                                                label="Complaints manager"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="accountant"
                                                label="Accountant"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="email"
                                                label="Email"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="approval"
                                                label="Approval"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <NullableSelectField
                                                name="timezone"
                                                label="Timezone"
                                                options={Object.keys(TimezoneEnum)
                                                    .sort()
                                                    .map((option) => ({ key: option, value: option }))}
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        {/* Column 2 */}
                                        <div className="w-full md:w-4/12 px-2 mb-2">
                                            <TextField
                                                name="number"
                                                label="Number"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={true}
                                            />
                                            <DateField
                                                className="w-full mb-2"
                                                name="inLedgerTradeDate"
                                                label="In ledger trade date"
                                                disabled={isSubmitting}
                                            />
                                            <SelectField
                                                name="issuerType"
                                                label="Issuer type"
                                                options={["None", "Government", "EuCovered", "CreditInstitution"]}
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                                size={12}
                                            />
                                            <div className="mb-2">
                                                <label>Types</label>
                                                <FieldArray
                                                    name="types"
                                                    render={(arrayHelpers) => (
                                                        <Fragment>
                                                            {values.types && values.types.length > 0 ? (
                                                                values.types.map((item, index) => (
                                                                    <div key={index} className="mb-4 w-full flex flex-wrap -mx-2">
                                                                        <div className="w-full mb-2">
                                                                            <SelectField
                                                                                className=""
                                                                                name={`types[${index}]`}
                                                                                label={null}
                                                                                options={Object.values(PartyType).sort()}
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mb-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mb-2"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(
                                                                                    index,
                                                                                    Object.values(PartyType)[0]
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() => arrayHelpers.push(Object.values(PartyType)[0])}
                                                                >
                                                                    +
                                                                </Button>
                                                            )}
                                                        </Fragment>
                                                    )}
                                                />
                                            </div>
                                            <div className="w-full mb-2">
                                                <label>Credit ratings</label>
                                                <FieldArray
                                                    name="creditRatings"
                                                    render={(arrayHelpers) => (
                                                        <Fragment>
                                                            {values.creditRatings && values.creditRatings.length > 0 ? (
                                                                values.creditRatings.map((item, index) => (
                                                                    <div key={index} className="mb-4 w-full flex flex-wrap -mx-2">
                                                                        <div className="w-full mb-2">
                                                                            <div className="flex flex-wrap -mx-2">
                                                                                <SelectField
                                                                                    className="w-full md:w-7/12 mb-2"
                                                                                    name={`creditRatings[${index}].creditRatingAgencyId`}
                                                                                    label={null}
                                                                                    options={data.creditRatingAgencies.map((d) => ({
                                                                                        key: d._id,
                                                                                        value: d.name
                                                                                    }))}
                                                                                    disabled={false}
                                                                                />
                                                                                <TextField
                                                                                    className="w-full md:w-5/12 mb-2"
                                                                                    name={`creditRatings[${index}].grade`}
                                                                                    label={null}
                                                                                    disabled={false}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mb-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mb-2"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(index, defaultCreditRating)
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() => arrayHelpers.push(defaultCreditRating)}
                                                                >
                                                                    +
                                                                </Button>
                                                            )}
                                                        </Fragment>
                                                    )}
                                                />
                                            </div>
                                            <div className="w-full mb-2">
                                                <label>Credit rating scale</label>
                                                <FieldArray
                                                    name="creditRatingScale"
                                                    render={(arrayHelpers) => (
                                                        <Fragment>
                                                            {values.creditRatingScale && values.creditRatingScale.length > 0 ? (
                                                                values.creditRatingScale.map((item, index) => (
                                                                    <div key={index} className="mb-4 w-full flex flex-wrap -mx-2">
                                                                        <div className="w-full mb-2">
                                                                            <TextField
                                                                                className=""
                                                                                name={`creditRatingScale[${index}]`}
                                                                                label={null}
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mb-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mb-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.insert(index, "")}
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() => arrayHelpers.push("")}
                                                                >
                                                                    +
                                                                </Button>
                                                            )}
                                                        </Fragment>
                                                    )}
                                                />
                                            </div>
                                            <SelectField
                                                className="w-full mb-2"
                                                name={"accountingCurrency"}
                                                label="Accounting currency"
                                                options={Object.values(CurrencyEnum).sort()}
                                                disabled={isSubmitting}
                                            />
                                            <SelectField
                                                className="w-full mb-2"
                                                name={"accountingFrequency"}
                                                label="Accounting frequency"
                                                options={Object.values(AccountingFrequencyEnum).sort()}
                                                disabled={isSubmitting}
                                            />
                                            <SelectField
                                                className="w-full mb-2"
                                                name={"accountingCompanyType"}
                                                label="Accounting company type"
                                                options={Object.values(AccountingCompanyTypeEnum).sort()}
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                className="w-full mb-2"
                                                name={"sustainalyticsId"}
                                                label="Sustainalytics id"
                                                type="text"
                                                disabled={isSubmitting}
                                            />
                                            <SearchListField
                                                className="w-full mb-2"
                                                name={"nace"}
                                                label={"Nace"}
                                                options={NaceOptions}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        {/* Column 3 */}
                                        <div className="w-full md:w-4/12 px-2 mb-2">
                                            <SearchListField
                                                className="w-full mb-2"
                                                name="parentId"
                                                label={
                                                    values.parentId !== "000000000000000000000000" ? (
                                                        <Link to={"/parties/" + values.parentId}>Parent</Link>
                                                    ) : (
                                                        <div>Parent</div>
                                                    )
                                                }
                                                options={parties}
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="legalEntityIdentifier"
                                                label={
                                                    <a
                                                        href={`https://search.gleif.org/#/record/${values.legalEntityIdentifier}`}
                                                        target="_blank"
                                                    >
                                                        Legal entity identifier
                                                    </a>
                                                }
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="legalNumber"
                                                label={
                                                    /^\d{6}-\d{4}$/.test(values.legalNumber || "") ? (
                                                        <a
                                                            href={"https://www.allabolag.se/bransch-s%C3%B6k?q=" + values.legalNumber}
                                                            target={"_blank"}
                                                            rel="noreferrer"
                                                        >
                                                            Legal number
                                                        </a>
                                                    ) : (
                                                        "Legal number"
                                                    )
                                                }
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="regulator"
                                                label="Regulator"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="businessIdentifierCode"
                                                label="Business Identifier Code (BIC)"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="industryCode"
                                                label="Industry code"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="bloombergEquityTicker"
                                                label="Bloomberg equity ticker"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="beneficialOwner._id"
                                                label="Beneficial owner id"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="beneficialOwner.name"
                                                label="Beneficial owner name"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                            />
                                            <SelectField
                                                name="status"
                                                label="Status"
                                                options={Object.keys(PartyStatusEnum)}
                                                className="w-full mb-2"
                                                disabled={isSubmitting}
                                                size={12}
                                            />
                                            <TextField
                                                name="updateTimestamp"
                                                label="Update timestamp"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={true}
                                            />
                                            <TextField
                                                name="updateUserInfo.name"
                                                label="Update user info"
                                                type="text"
                                                className="w-full mb-2"
                                                disabled={true}
                                            />
                                            {values.types && values.types.includes(PartyType.Client) ? (
                                                <div className="w-full flex flex-wrap -mx-2 ml-1 mb-2">
                                                    <Link to={"/transactions?clientIds=" + values._id + "&startDate=2000-01-01"}>
                                                        {"Transactions"}
                                                    </Link>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full px-2 mb-2">
                                            <label style={{ fontWeight: "bold" }}>Standard settlement instructions</label>
                                            <FieldArray
                                                name="standardSettlementInstructions"
                                                render={(arrayHelpers) => (
                                                    <Fragment>
                                                        {values.standardSettlementInstructions &&
                                                        values.standardSettlementInstructions.length > 0 ? (
                                                            values.standardSettlementInstructions.map((item, index) => (
                                                                <div key={index} className="mb-4 w-full flex flex-wrap -mx-2">
                                                                    <div className="w-full md:w-10/12 px-2 mb-2">
                                                                        <fieldset>
                                                                            <legend>{index + 1}</legend>
                                                                            <TextField
                                                                                name={`standardSettlementInstructions[${index}].agentBusinessIdentifierCode`}
                                                                                label="Agent business identifier code* (BIC)"
                                                                                className="text-green-600"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`standardSettlementInstructions[${index}].beneficiaryBusinessIdentifierCode`}
                                                                                label="Beneficiary business identifier code* (BIC)"
                                                                                className=""
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`standardSettlementInstructions[${index}].iban`}
                                                                                label="IBAN"
                                                                                className=""
                                                                                disabled={false}
                                                                            />
                                                                            <SelectField
                                                                                className=""
                                                                                name={`standardSettlementInstructions[${index}].placeOfSettlementId`}
                                                                                label={
                                                                                    <Link
                                                                                        to={
                                                                                            "/parties/" +
                                                                                            values.standardSettlementInstructions[
                                                                                                index
                                                                                            ].placeOfSettlementId
                                                                                        }
                                                                                    >
                                                                                        Place of settlement*
                                                                                    </Link>
                                                                                }
                                                                                options={data.placesOfSettlement.map((d) => ({
                                                                                    key: d._id,
                                                                                    value: d.name
                                                                                }))}
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`standardSettlementInstructions[${index}].securityAccount`}
                                                                                label="Security account"
                                                                                className=""
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`standardSettlementInstructions[${index}].buyerSellerSafe`}
                                                                                label="Buyer seller safe"
                                                                                className=""
                                                                                disabled={false}
                                                                            />
                                                                            <SelectField
                                                                                className=""
                                                                                name={`standardSettlementInstructions[${index}].type`}
                                                                                label="Type*"
                                                                                options={Object.values(
                                                                                    PartyStandardSettlementInstructionType
                                                                                ).map((d) => ({
                                                                                    key: d,
                                                                                    value: d
                                                                                }))}
                                                                                disabled={false}
                                                                            />{" "}
                                                                            <SelectField
                                                                                className=""
                                                                                name={`standardSettlementInstructions[${index}].tradeType`}
                                                                                label="Trade type*"
                                                                                options={Object.values(SsiTradeType).map((d) => ({
                                                                                    key: d,
                                                                                    value: d
                                                                                }))}
                                                                                disabled={false}
                                                                            />
                                                                        </fieldset>
                                                                    </div>
                                                                    <div className="w-full md:w-2/12 px-2 mb-2">
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mt-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mt-2"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(
                                                                                    index,
                                                                                    defaultSettlementInstruction
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="ml-1"
                                                                type="button"
                                                                onClick={() => arrayHelpers.push(defaultSettlementInstruction)}
                                                            >
                                                                +
                                                            </Button>
                                                        )}
                                                    </Fragment>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full px-2 mb-2">
                                            <label style={{ fontWeight: "bold" }}>Aliases</label>
                                            <FieldArray
                                                name="aliases"
                                                render={(arrayHelpers) => (
                                                    <Fragment>
                                                        {values.aliases && values.aliases.length > 0 ? (
                                                            values.aliases.map((alias, index) => (
                                                                <div key={index} className="mb-4 w-full flex flex-wrap -mx-2">
                                                                    <div className="w-full md:w-10/12 px-2 mb-2">
                                                                        <fieldset>
                                                                            <legend>{index + 1}</legend>
                                                                            <SelectField
                                                                                name={`aliases[${index}].collection`}
                                                                                label="Collection"
                                                                                className="text-green-600"
                                                                                options={Object.values(CollectionNameEnum)}
                                                                                disabled={isSubmitting}
                                                                                size={5}
                                                                            />
                                                                            <SearchListField
                                                                                className=""
                                                                                name={`aliases[${index}].documentId`}
                                                                                label={aliasLabel(
                                                                                    alias.collection,
                                                                                    alias.documentId
                                                                                )}
                                                                                options={aliasOptions(alias.collection)}
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                className=""
                                                                                name={`aliases[${index}].key`}
                                                                                label="Key"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                className=""
                                                                                name={`aliases[${index}].value`}
                                                                                label="Value"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                className=""
                                                                                name={`aliases[${index}].comment`}
                                                                                label="Comment"
                                                                                disabled={false}
                                                                            />
                                                                        </fieldset>
                                                                    </div>
                                                                    <div className="w-full md:w-2/12 px-2 mb-2">
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mt-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mt-2"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.insert(index, defaultAlias)}
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="ml-1"
                                                                type="button"
                                                                onClick={() => arrayHelpers.push(defaultAlias)}
                                                            >
                                                                +
                                                            </Button>
                                                        )}
                                                    </Fragment>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full px-2 mb-4">
                                            {alert.visible ? (
                                                <Alert
                                                    style={{ marginTop: "10px" }}
                                                    variant={alert.color}
                                                    onClose={onDismissAlert}
                                                    dismissible
                                                >
                                                    {alert.message}
                                                </Alert>
                                            ) : null}
                                            <SubmitButton disabled={isSubmitting} label={isEdit ? "Update" : "Create"} />
                                        </div>
                                    </div>
                                </Form>
                            </Fragment>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};
